import { useMemo } from 'react';

import * as api from '@gitbook/api-client';
import { useDateRefresh } from '@gitbook/util-react-hooks';

import { calculateDaysDifference } from './common';

export type OrgTrialDetails = {
    status: api.BillingTrialStatus;
    daysLeft?: number;
};

/**
 * A hook to return details about an organization trial.
 */
export function useTrial(props: { organization: api.Organization }): OrgTrialDetails {
    const { organization } = props;
    const daysLeft = useRemainingTrialDays(organization);

    return useMemo(
        () => ({ status: organization.trial.status, daysLeft }),
        [organization.trial.status, daysLeft]
    );
}

/**
 * A hook to get the number of days (rounded up) remaining on the free trial with auto-refresh.
 */
function useRemainingTrialDays(organization: api.Organization): number | undefined {
    const trialEnd = organization.trial.endDate ? new Date(organization.trial.endDate) : undefined;

    useDateRefresh(trialEnd ?? null);

    if (!trialEnd || organization.trial.status !== api.BillingTrialStatus.Active) {
        return undefined;
    }

    return calculateDaysDifference(new Date(), trialEnd);
}
